import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { X, Phone, Mail, MapPin, Send, User } from 'lucide-react';
import apiClient from '@/utils/apiClient';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

// Memoized Notification Form Component
const NotificationForm = memo(({ student, onSend }) => {
  const [message, setMessage] = useState({
    title: '',
    content: '',
    category: 'general'
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.title || !message.content) {
      toast.error('Please fill in all fields');
      return;
    }

    setSending(true);
    try {
      await apiClient.post('/v1/admin/announcements/', {
        title: message.title,
        message: message.content,
        category: message.category,
        priority: 'normal',
        target_all: false,
        target_classes: [],
        target_rooms: [],
        target_students: [student.id],
        target_warden_id: null,
        attachments: [],
        scheduled_time: null,
        send_now: true,
        hostel_id: student.hostel_id
      });
      
      toast.success('Notification sent successfully to parent!');
      setMessage({ title: '', content: '', category: 'general' });
      onSend?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">Category</label>
        <select
          value={message.category}
          onChange={(e) => setMessage(prev => ({ ...prev, category: e.target.value }))}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="general">General</option>
          <option value="financial">Financial</option>
          <option value="holidays">Holidays</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Title</label>
        <input
          type="text"
          value={message.title}
          onChange={(e) => setMessage(prev => ({ ...prev, title: e.target.value }))}
          placeholder={`e.g., Progress Update for ${student?.name || 'student'}`}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Message</label>
        <textarea
          value={message.content}
          onChange={(e) => setMessage(prev => ({ ...prev, content: e.target.value }))}
          placeholder="Write your message here..."
          rows={4}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          required
        />
      </div>

      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => setMessage({ title: '', content: '', category: 'general' })}
        >
          Clear
        </Button>
        <Button
          type="submit"
          disabled={sending}
          className="btn-primary text-white"
        >
          {sending ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Sending...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Send Notification
            </>
          )}
        </Button>
      </div>
    </form>
  );
});

NotificationForm.displayName = 'NotificationForm';

const ParentDetailsModal = ({ isOpen, onClose, student }) => {
  const [parent, setParent] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && student?.parent_id) {
      fetchParentDetails();
    }
  }, [isOpen, student?.parent_id]);

  const fetchParentDetails = useCallback(async () => {
    if (!student?.parent_id) return;
    
    setLoading(true);
    try {
      const response = await apiClient.get(`/v1/admin/parents/${student.parent_id}`);
      setParent(response.data);
    } catch (error) {
      toast.error('Failed to load parent details');
    } finally {
      setLoading(false);
    }
  }, [student?.parent_id]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold" style={{ color: '#2C5F7F' }}>
            Parent Details
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="loading-spinner"></div>
            </div>
          ) : parent ? (
            <>
              {/* Student Info */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-2" style={{ color: '#2C5F7F' }}>
                  Student Information
                </h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-600">Name:</span>
                    <span className="ml-2 font-semibold">{student.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Class:</span>
                    <span className="ml-2 font-semibold">{student.class_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Room:</span>
                    <span className="ml-2 font-semibold">{student.room_number}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">ID:</span>
                    <span className="ml-2 font-semibold">{student.readable_id}</span>
                  </div>
                </div>
              </div>

              {/* Parent Details */}
              <div>
                <h3 className="font-semibold text-lg mb-4" style={{ color: '#2C5F7F' }}>
                  Parent/Guardian Information
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                      <User className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-lg">{parent.name}</p>
                      <p className="text-sm text-gray-600">Parent/Guardian</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Phone className="w-5 h-5" style={{ color: '#5A9FD4' }} />
                    <div>
                      <p className="text-xs text-gray-600">Phone Number</p>
                      <p className="font-semibold">{parent.phone}</p>
                    </div>
                  </div>

                  {parent.email && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Mail className="w-5 h-5" style={{ color: '#5A9FD4' }} />
                      <div>
                        <p className="text-xs text-gray-600">Email Address</p>
                        <p className="font-semibold">{parent.email}</p>
                      </div>
                    </div>
                  )}

                  {parent.address && (
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <MapPin className="w-5 h-5 mt-1" style={{ color: '#5A9FD4' }} />
                      <div>
                        <p className="text-xs text-gray-600">Address</p>
                        <p className="font-semibold">{parent.address}</p>
                      </div>
                    </div>
                  )}

                  {parent.children_count !== undefined && (
                    <div className="p-3 bg-green-50 rounded-lg">
                      <p className="text-sm text-gray-600">Total Children in Hostel</p>
                      <p className="text-2xl font-bold" style={{ color: '#5A9FD4' }}>
                        {parent.children_count}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Send Notification Section */}
              <div className="border-t pt-6">
                <h3 className="font-semibold text-lg mb-4" style={{ color: '#2C5F7F' }}>
                  Send Notification to Parent
                </h3>
                <NotificationForm student={student} />
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No parent information available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParentDetailsModal;
